import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Truck, Plane, Headphones, BadgeDollarSign, Star } from "lucide-react";
import { HERO_SLIDES, PROMISES, PRODUCTS, REVIEWS, BLOG_POSTS, BRAND_LOGOS } from "../mock";
import { useStorefront, storefrontCategories, categoryImage, isHiddenOnSite } from "../lib/storefront";
import NewsletterPopup from "../components/NewsletterPopup";
import LeadCaptureModal from "../components/LeadCaptureModal";

const ICONS = { Truck, Plane, Headphones, BadgeDollarSign };

function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 7500);
    return () => clearInterval(t);
  }, []);
  const slide = HERO_SLIDES[idx];

  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: slide.bg }}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center min-h-[480px] lg:min-h-[540px]">
        <div className="order-2 lg:order-1">
          <p className="text-xs tracking-[0.2em] font-semibold text-[#2C66E3] mb-4">{slide.eyebrow}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight mb-5">
            {slide.title}
          </h1>
          <p className="text-lg text-neutral-700 max-w-md mb-8 leading-relaxed">{slide.subtitle}</p>
          <Link
            to={slide.href}
            className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#2C66E3] text-white px-7 py-3.5 rounded-full font-semibold text-[15px] transition-colors"
          >
            {slide.cta}
          </Link>
        </div>
        <div className="order-1 lg:order-2 relative flex justify-center">
          <div
            className={`${
              slide.aspect === "portrait"
                ? "aspect-[5/6]"
                : slide.fit === "contain"
                ? "aspect-square max-w-[560px]"
                : "aspect-[4/3]"
            } w-full rounded-2xl overflow-hidden shadow-2xl bg-white`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        onClick={() => setIdx((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center transition"
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => setIdx((i) => (i + 1) % HERO_SLIDES.length)}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center transition"
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* dots */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#1a1a1a]" : "w-1.5 bg-neutral-400"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="border-y border-neutral-200 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-3 flex items-center justify-center gap-2 text-sm flex-wrap">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]" />
          ))}
        </div>
        <span className="font-semibold">Excellent</span>
        <span className="text-neutral-600">4.9 / 5 — based on <strong>500+</strong> reviews on </span>
        <a
          href="https://share.google/ILEFK6kdeMiUTWbLl"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-bold hover:underline"
          aria-label="Read Let’s Print reviews on Google"
        >
          <span className="text-[#4285F4]">G</span>
          <span className="text-[#EA4335]">o</span>
          <span className="text-[#FBBC04]">o</span>
          <span className="text-[#4285F4]">g</span>
          <span className="text-[#34A853]">l</span>
          <span className="text-[#EA4335]">e</span>
        </a>
      </div>
    </section>
  );
}

function Promises() {
  return (
    <section className="bg-[#FAF7F2] py-12">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {PROMISES.map((p) => {
          const Icon = ICONS[p.icon];
          return (
            <div key={p.title} className="text-center">
              <div className="w-14 h-14 rounded-full bg-white shadow-sm mx-auto mb-4 flex items-center justify-center">
                <Icon className="w-6 h-6 text-[#2C66E3]" />
              </div>
              <h3 className="font-bold text-[#1a1a1a] text-[15px] mb-1.5">{p.title}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed max-w-[220px] mx-auto">{p.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CategoryGrid() {
  // Category tiles come from the CRM (website_categories).
  const data = useStorefront();
  const cats = storefrontCategories(data)
    .filter((c) => c.show_on_home)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1a1a1a] tracking-tight mb-3">Shop our products</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">From classic business cards to large-format banners — quality print for every brand.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6">
          {cats.map((cat) => (
            <Link key={cat.slug} to={`/category/${cat.slug}`} className="group">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-100 mb-3">
                {categoryImage(cat) ? (
                  <img src={categoryImage(cat)} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EEF3FF] to-[#E3E8F2] text-[#1a1a1a] font-black text-xl text-center px-4">{cat.name}</div>
                )}
              </div>
              <p className="font-bold text-[#1a1a1a] group-hover:text-[#2C66E3] transition-colors">
                Shop {cat.name} <span className="inline-block ml-1 group-hover:translate-x-1 transition-transform">→</span>
              </p>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white rounded-full font-semibold text-sm transition-colors">
            Shop All Let’s Print Products
          </Link>
        </div>
      </div>
    </section>
  );
}

function PromoBlock() {
  const [openLead, setOpenLead] = useState(null); // "apparel" | "vps" | null

  const meta = {
    apparel: {
      modalTitle: "Launch Your Apparel Brand with Let’s Print",
      modalSubtitle: "Fill in your details and our team will share custom packages, sample kits and design support — within 1 business day.",
      eyebrow: "APPAREL BRAND",
    },
    vps: {
      modalTitle: "Open Your Virtual Print Shop",
      modalSubtitle: "Tell us about your business and we'll walk you through our white-label catalog, pricing and onboarding.",
      eyebrow: "VIRTUAL PRINT SHOP",
    },
  };

  return (
    <section className="py-12 lg:py-20">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div
          className="relative w-full overflow-hidden rounded-3xl"
          style={{ aspectRatio: "1409/411" }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_custom-print-flow/artifacts/1fi5rlzx_image.png"
            alt="Start your apparel brand or your own Virtual Print Shop — flat 25% off"
            className="absolute select-none"
            style={{
              width: "124.5%",
              left: "-10.7%",
              top: "-19.95%",
              maxWidth: "none",
            }}
            draggable="false"
          />

          {/* Invisible overlays on the printed "Know more" buttons */}
          <button
            onClick={() => setOpenLead("apparel")}
            aria-label="Apparel brand — Know more"
            className="absolute z-10 bg-transparent hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            style={{ left: "4%", bottom: "8%", width: "10%", height: "20%" }}
          />
          <button
            onClick={() => setOpenLead("vps")}
            aria-label="Virtual Print Shop — Know more"
            className="absolute z-10 bg-transparent hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            style={{ left: "50%", bottom: "8%", width: "10%", height: "20%" }}
          />
        </div>
      </div>

      <LeadCaptureModal
        open={openLead !== null}
        onClose={() => setOpenLead(null)}
        title={meta[openLead]?.modalTitle || ""}
        subtitle={meta[openLead]?.modalSubtitle || ""}
        eyebrow={meta[openLead]?.eyebrow || "GET STARTED"}
      />
    </section>
  );
}

function BrandStrip() {
  return (
    <section className="py-14 border-y border-neutral-200">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <p className="text-center text-xs tracking-[0.2em] font-semibold text-neutral-500 mb-10">TRUSTED BY BRANDS LARGE &amp; SMALL</p>
        <div className="flex items-center justify-center gap-x-6 sm:gap-x-10 md:gap-x-14 lg:gap-x-16 gap-y-6 flex-wrap">
          {BRAND_LOGOS.map((b) => (
            <div
              key={b.name}
              className="flex items-center justify-center w-[120px] h-[80px] md:w-[140px] md:h-[90px] hover:scale-105 transition-transform duration-300"
              title={b.name}
            >
              <img
                src={b.logo}
                alt={b.name}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                draggable="false"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} className="w-5 h-5 fill-[#00B67A] text-[#00B67A]" />
            ))}
          </div>
          <h3 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">Loved by 10,000+ businesses in South Florida</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {REVIEWS.slice(0, 6).map((r, i) => (
            <div key={i} className="p-6 rounded-2xl border border-neutral-200 bg-white hover:shadow-lg transition-shadow">
              <div className="flex gap-0.5 mb-3">
                {[...Array(r.rating)].map((_, k) => (
                  <Star key={k} className="w-4 h-4 fill-[#00B67A] text-[#00B67A]" />
                ))}
              </div>
              <p className="text-[15px] text-neutral-800 leading-relaxed mb-4">“{r.text}”</p>
              <p className="text-sm font-semibold text-[#1a1a1a]">{r.name}</p>
              <p className="text-xs text-neutral-500">{r.date}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogSection() {
  return (
    <section className="py-16 lg:py-24 bg-[#FAF7F2]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <h3 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">From the blog</h3>
          <Link to="/blog" className="hidden md:inline-block text-sm font-semibold underline underline-offset-4 hover:text-[#2C66E3]">
            All articles
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {BLOG_POSTS.map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="group" data-testid={`blog-card-${b.slug}`}>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-4 bg-neutral-100">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <p className="text-xs text-neutral-500 mb-1">{b.date}</p>
              <h4 className="font-bold text-lg text-[#1a1a1a] group-hover:text-[#2C66E3] transition-colors mb-2">{b.title}</h4>
              <p className="text-sm text-neutral-600 leading-relaxed">{b.excerpt}</p>
              <span className="inline-block mt-3 text-sm font-semibold underline underline-offset-4">Read more</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TopSelling() {
  const data = useStorefront();
  const TOP_SELLER_SLUGS = [
    "regular-business-card",
    "flyers",
    "car-magnets",
    "retractable-banner",
    "indoor-banner",
    "waterproof-vinyl-labels",
  ];
  const top = TOP_SELLER_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug)).filter((p) => p && !isHiddenOnSite(p.slug, data));
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h3 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">Top sellers</h3>
            <p className="text-neutral-600 mt-2">Customer favorites, ready to make yours.</p>
          </div>
          <Link to="/shop" className="hidden md:inline-block text-sm font-semibold underline underline-offset-4 hover:text-[#2C66E3]">
            Shop all
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {top.map((p) => (
            <Link key={p.slug} to={`/product/${p.slug}`} className="group" data-testid={`top-seller-${p.slug}`}>
              <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-100 mb-3">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <h4 className="font-bold text-[#1a1a1a] group-hover:text-[#2C66E3] transition-colors text-[15px]">{p.name}</h4>
              <p className="text-sm text-neutral-600 mt-0.5">From <span className="font-semibold text-[#1a1a1a]">${p.fromPrice}</span></p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const onSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail("");
    }
  };
  return (
    <section className="py-16 lg:py-24 bg-[#1a1a1a] text-white">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 text-center">
        <p className="text-xs tracking-[0.2em] font-semibold text-[#2C66E3] mb-3">NEW TO LET’S PRINT? GET 20% OFF</p>
        <h3 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight mb-4">
          Sign up for special offers, news and inspiration
        </h3>
        <p className="text-neutral-400 mb-8 max-w-lg mx-auto">Join the Let’s Print newsletter for 20% off your first order, design tips, and exclusive offers.</p>
        {submitted ? (
          <p className="text-[#2C66E3] font-semibold">Thanks! Check your inbox to confirm.</p>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-5 py-3.5 rounded-full bg-white text-black outline-none focus:ring-2 focus:ring-[#2C66E3]"
            />
            <button type="submit" className="px-7 py-3.5 rounded-full bg-[#2C66E3] hover:bg-[#1E54C4] font-semibold text-sm transition-colors">
              Sign up
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <NewsletterPopup />
      <HeroCarousel />
      <TrustBar />
      <Promises />
      <CategoryGrid />
      <PromoBlock />
      <TopSelling />
      <BrandStrip />
      <Reviews />
      <BlogSection />
      <Newsletter />
    </>
  );
}
